import clsx from "clsx";
import { Fragment, useState, forwardRef, useImperativeHandle } from "react";
import { Toaster } from "react-hot-toast";
import { Dialog, Transition } from "@headlessui/react";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { ErrorText } from "components/atoms/ErrorText";

const ChatForm = forwardRef(({ childRef }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const initialValues = { fullname: "", email: "", message: "" };

    useImperativeHandle(childRef, () => ({
        openModal() {
            openModal();
        }
    }));

    function closeModal() {
        setIsOpen(false);
    }

    function openModal() {
        setIsOpen(true);
    }

    const schemaValidations = Yup.object({
        fullname: Yup.string()
            .required("Field fullname Required"),
        email: Yup.string()
            .email("Invalid Email Address")
            .required("Field Email Required"),
        message: Yup.string()
            .min(20, "Minimum 20 character length")
            .required("Field Message Required")
    });

    const SendMessageHandler = (values) => {
        setLoading(true);
        try {
            const message = "Thank you! Your message is sent successfully!";
            setTimeout(() => {
                closeModal();
            }, 2000);
            setTimeout(() => {
                alert(message);
                console.log(values);

                setLoading(false);
            }, 5000);
        } catch (error) {
            console.log(error);
            setLoading(false);

            alert("Send Message Failed");
        }
    };

    return (
        <>
            <Transition appear show={isOpen} as={Fragment}>
                <Dialog
                    as="div"
                    className="fixed inset-0 z-[88888] overflow-y-auto"
                    onClose={closeModal}
                >
                    <div className="min-h-screen px-5 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0"
                            enterTo="opacity-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <Dialog.Overlay className="fixed inset-0 bg-[#0C1B3A]/50" />
                        </Transition.Child>

                        <span
                            className="inline-block h-screen align-middle"
                            aria-hidden="true"
                        >
                            &#8203;
                        </span>

                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <div className={clsx(
                                "inline-block w-full my-14 md:my-[86px] text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl divide-y-[1px] divide-neutral-30",
                                "md:w-[548px]"
                            )}>
                                <div className="m-[24px] flex justify-between items-center">
                                    <div>
                                        <Dialog.Title
                                            as="p"
                                            className="text-[18px] font-semibold text-neutral-100 leading-[27px] antialiased"
                                        >
                                            Send Us a Message
                                        </Dialog.Title>
                                    </div>

                                    <button onClick={() => setIsOpen(false)}>
                                        <svg width="24" height="24" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path fillRule="evenodd" clipRule="evenodd" d="M4.29289 4.29289C4.68342 3.90237 5.31658 3.90237 5.70711 4.29289L10 8.58579L14.2929 4.29289C14.6834 3.90237 15.3166 3.90237 15.7071 4.29289C16.0976 4.68342 16.0976 5.31658 15.7071 5.70711L11.4142 10L15.7071 14.2929C16.0976 14.6834 16.0976 15.3166 15.7071 15.7071C15.3166 16.0976 14.6834 16.0976 14.2929 15.7071L10 11.4142L5.70711 15.7071C5.31658 16.0976 4.68342 16.0976 4.29289 15.7071C3.90237 15.3166 3.90237 14.6834 4.29289 14.2929L8.58579 10L4.29289 5.70711C3.90237 5.31658 3.90237 4.68342 4.29289 4.29289Z" fill="#808896" />
                                        </svg>
                                    </button>
                                </div>

                                {/* Form */}
                                <Formik initialValues={initialValues} validationSchema={schemaValidations}>
                                    {({ errors, touched }) => (
                                        <div className="divide-y-0">
                                            <Form className="w-full px-6 grid gap-[20px] py-[32px] phone:pt-[24px] phone:pb-[32px]">
                                                <div>
                                                    <h3 className="font-medium text-md text-neutral-50 mb-4 tracking-text leading-[16px]">Full Name</h3>
                                                    <Field type="text" name="fullname" className={clsx(
                                                        "w-full h-[56px] px-4 bg-neutral-20 rounded-lg",
                                                        errors.fullname && touched.fullname ? "border border-red-600 !bg-neutral-10" : "border-neutral-20"
                                                    )} />
                                                    <ErrorMessage name="fullname" component={ErrorText} />
                                                </div>
                                                <div>
                                                    <h3 className="font-medium text-md text-neutral-50 mb-4 tracking-text leading-[16px]">Email</h3>
                                                    <Field type="text" name="email" className={clsx(
                                                        "w-full h-[56px] px-4 bg-neutral-20 rounded-lg",
                                                        errors.email && touched.email ? "border border-red-600 !bg-neutral-10" : "border-neutral-20"
                                                    )} />
                                                    <ErrorMessage name="email" component={ErrorText} />
                                                </div>
                                                <div>
                                                    <h3 className="font-medium text-md text-neutral-50 mb-4 tracking-text leading-[16px]">Message</h3>
                                                    <Field type="text" name="message" id="message" rows="100" cols="50" className={clsx(
                                                        "w-full h-[150px] p-4 bg-neutral-20 rounded-lg resize-none",
                                                        errors.message && touched.message ? "border border-red-600 !bg-neutral-10" : "border-neutral-20"
                                                    )} />
                                                    <ErrorMessage name="message" component={ErrorText} />
                                                </div>
                                            </Form>
                                            <div className="p-[24px] pt-0">
                                                <button type="submit" onClick={SendMessageHandler} className="w-full hover:bg-primary-hover py-[18px] text-neutral-10 font-semibold text-md rounded-lg bg-primary-main leading-[19.83px]">{loading ? "Sending..." : "Send Message"}</button>
                                            </div>
                                        </div>
                                    )}
                                </Formik>
                            </div>
                        </Transition.Child>
                    </div>
                </Dialog>
            </Transition>
            <Toaster />
        </>
    );
});

ChatForm.displayName = "Send Us a Message";

export default ChatForm;
